import type { Page } from '@hello-aria/api-client'
import {
    UseInfiniteQueryResult,
    UseMutationResult,
    UseQueryResult,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    InfiniteData
} from '@tanstack/react-query'
import { useMe } from '../../../shared/hooks/useMe'
import { Company } from '../../../shared/models/companyModel'
import { QueryParams } from '../../../shared/models/queryModels'
import {
    CreateSubscription,
    DeleteSubscription,
    EditSubscription,
    Subscription
} from '../../../shared/models/subscriptionModels'
import { mapQuery } from '../../../shared/utils/queryUtils'
import { useQueryParamsState } from '../../../shared/hooks/useQueryParams'
import { resolveSubscriptionFilters } from '../utils/subscriptionUtils'
import { subscriptionTableFields } from '../enums/subscriptionEnums'
import { deprecated_useSetToast } from '../../../shared/hooks/useSetToast'
import {
    createSubscription,
    deleteSubscription,
    getSubscription,
    listSubscriptions,
    updateSubscription
} from '../../../shared/services/subscriptionsServices'

const subscriptionsQueryKeys = {
    root: ['infiniteSubscriptions'],
    infiniteSubscriptions: (
        companyId: Company['id'],
        queryParams?: QueryParams
    ): (string | QueryParams | undefined)[] => [...subscriptionsQueryKeys.root, companyId, queryParams],
    subscriptionById: (companyId: Company['id'], subscriptionId: string | undefined): (string | undefined)[] => [
        ...subscriptionsQueryKeys.root,
        companyId,
        subscriptionId
    ]
}

export const useInfiniteSubscriptions = (): UseInfiniteQueryResult<InfiniteData<Page<Subscription>>> => {
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()

    return useInfiniteQuery({
        queryKey: subscriptionsQueryKeys.infiniteSubscriptions(
            companyId,
            queryParamsState.pick([`subscriptions_search`, `subscriptions_sort`, `subscriptions_filter`])
        ),

        queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
            const mappedQuery = mapQuery({
                filter: resolveSubscriptionFilters({
                    customFilters: queryParamsState.get(`subscriptions_filter`)
                }),
                search: {
                    value: queryParamsState.get(`subscriptions_search`),
                    path: subscriptionTableFields
                },
                sortBy: queryParamsState.get(`subscriptions_sort`),
                pagination: { after: pageParam }
            })

            return listSubscriptions(companyId, mappedQuery)
        },

        getNextPageParam: (lastPage) => {
            if (lastPage.pagination.hasNextPage) return lastPage.pagination.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

export const useSubscriptionById = (subscriptionId: string | undefined): UseQueryResult<Subscription | undefined> => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: subscriptionsQueryKeys.subscriptionById(companyId, subscriptionId),
        queryFn: () => getSubscription(companyId, subscriptionId)
    })
}

export const useCreateSubscription = (): UseMutationResult<Subscription, unknown, CreateSubscription> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const queryParams = useQueryParamsState()

    return useMutation({
        mutationFn: (payload: CreateSubscription) => createSubscription(companyId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKeys.infiniteSubscriptions(
                    companyId,
                    queryParams.pick([`subscriptions_search`, `subscriptions_sort`, `subscriptions_filter`])
                )
            })
        }
    })
}

export const useUpdateSubscription = (): UseMutationResult<Subscription, unknown, EditSubscription> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()
    const queryParams = useQueryParamsState()

    return useMutation({
        mutationFn: (payload: EditSubscription) => updateSubscription(companyId, payload),
        onSuccess: (response) => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKeys.subscriptionById(companyId, response.id)
            })
            queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKeys.infiniteSubscriptions(
                    companyId,
                    queryParams.pick([`subscriptions_search`, `subscriptions_sort`, `subscriptions_filter`])
                )
            })
        }
    })
}

export const useDeleteSubscription = (): UseMutationResult<void, unknown, DeleteSubscription> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()
    const setToast = deprecated_useSetToast()

    const queryParams = useQueryParamsState()

    return useMutation({
        mutationFn: (payload: DeleteSubscription) => deleteSubscription(companyId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKeys.infiniteSubscriptions(
                    companyId,
                    queryParams.pick([`subscriptions_search`, `subscriptions_sort`, `subscriptions_filter`])
                )
            })
            setToast('deleteSubscription', 'positive')
        },
        onError: () => setToast('deleteSubscription', 'negative')
    })
}
